import React, { FC, ReactNode, useCallback, useEffect, useMemo } from 'react'
import { Modal } from '@lightspeed/design-system-react'
import { vn } from 'vend-number'
import { EcomApiResponse, EcomRefundModalOptions } from './helpers/types'
import { useRefundEffectReducer } from './hooks/use-refund-effect-reducer/useRefundEffectReducer'
import { ErrorContent } from './components/ErrorContent'
import { Processing } from './components/Processing'
interface EcomRefundModalProps extends EcomRefundModalOptions {
  close: (val?: Promise<EcomApiResponse>) => void
}

export const EcomRefundModal: FC<EcomRefundModalProps> = ({
  amount: rawAmount,
  originalSaleId,
  registerId,
  registerSalePaymentId,
  close: originalClose,
}) => {
  const amount = useMemo(() => vn(rawAmount), [rawAmount])
  const [refundState, dispatch] = useRefundEffectReducer({
    amount,
    originalSaleId,
    registerId,
    registerSalePaymentId,
  })
  const close = useCallback(
    (val?: Promise<EcomApiResponse>) => {
      originalClose(val)
    },
    [originalClose]
  )
  useEffect(() => {
    if (refundState.status === 'done') {
      close(Promise.resolve({ success: true }))
    }
  }, [close, refundState])

  const onError = useCallback(() => {
    // close the popup with no argument - it is picked up as a failure
    close()
  }, [close])

  const getRefundContent = (): ReactNode => {
    switch (refundState.status) {
      case 'refunding': {
        return <Processing />
      }
      case 'done': {
        // Modal should be closed if the state was done.
        // Hence we can just return
        return
      }
      case 'error':
        return (
          <ErrorContent
            ecomError={refundState.errorMessage}
            isRetryable={refundState.retryable}
            retry={() => dispatch('RETRY')}
            close={() => {
              onError()
            }}
          />
        )
      default: {
        // This `never` typing will let us know at compile time if we do not handle an existing event type.
        const _exhaustiveCheck: never = refundState
        return _exhaustiveCheck
      }
    }
  }
  const isDissmissable = refundState.status === 'error'
  return (
    <Modal
      size="medium"
      focusTrapActive={true}
      onClose={onError}
      dismissable={isDissmissable}
    >
      {getRefundContent()}
    </Modal>
  )
}
